import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CircleTwoToneIcon from '@mui/icons-material/CircleTwoTone';
import { Button, Modal } from '@mui/material';
import React, { useState } from 'react';
import Skeleton from 'react-loading-skeleton';
import { PhotoAlbum, RenderPhoto, RenderPhotoProps } from 'react-photo-album';
import Lightbox from 'yet-another-react-lightbox';
import Counter from 'yet-another-react-lightbox/plugins/counter';
import 'yet-another-react-lightbox/plugins/counter.css';
import Thumbnails from 'yet-another-react-lightbox/plugins/thumbnails';
import 'yet-another-react-lightbox/plugins/thumbnails.css';
import Zoom from 'yet-another-react-lightbox/plugins/zoom';
import 'yet-another-react-lightbox/styles.css';
import 'react-loading-skeleton/dist/skeleton.css';

import useMobileMediaQuery from '../../../hooks/useMobileMediaQuery';
import './CardImageGallery.scss';

type CardImageGalleryProps = {
  imgUrls: string[];
  /** True while the full photo list is still resolving. */
  isLoading?: boolean;
  /** Tiles to reserve while loading, so the grid keeps its final size. */
  placeholderCount?: number;
  isEdit: boolean;
  galleryOpen?: boolean;
  handleGalleryClose?: () => void;
  originalSelectedImgIndex?: number | null;
  submitCoverImageChange?: (newCoverImage: number) => void;
};

function CardImageGallery({
  imgUrls,
  isLoading = false,
  placeholderCount = 0,
  isEdit,
  galleryOpen = false,
  handleGalleryClose,
  originalSelectedImgIndex = 0,
  submitCoverImageChange,
}: CardImageGalleryProps) {
  const [index, setIndex] = useState(-1);
  const [selectedIndex, setSelectedIndex] = useState<number | null>(originalSelectedImgIndex || 0);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);
  const isMobile = useMobileMediaQuery();

  const photos = imgUrls.map((src) => ({ src, width: 500, height: 500 }));

  const showPlaceholders = isLoading && placeholderCount > imgUrls.length;

  const handlePhotoClick = (current: number) => {
    setIndex(current);
    if (isEdit) {
      setSelectedIndex(current === selectedIndex ? null : current);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent, idx: number) => {
    if (event.key === 'Enter') {
      handlePhotoClick(idx);
    }
  };

  const SelectedIndicator = () => (
    <div className='card-details-gallery-image'>
      <div style={{ backgroundColor: 'white', borderRadius: '50%' }}>
        <CheckCircleIcon style={{ color: '#2c969d', fontSize: '24px' }} />
      </div>
    </div>
  );

  // used to style the image and image container
  const renderPhoto: RenderPhoto = ({ imageProps, photo, layout }: RenderPhotoProps) => {
    const currentIndex = layout.index;
    const isSelected = selectedIndex === currentIndex;
    const isHovered = hoveredIndex === currentIndex;

    return (
      <div
        style={{ position: 'relative', alignItems: 'center' }}
        onMouseEnter={() => isEdit && setHoveredIndex(currentIndex)}
        onMouseLeave={() => isEdit && setHoveredIndex(null)}
        onClick={() => handlePhotoClick(currentIndex)}
        onKeyDown={(e) => handleKeyDown(e, currentIndex)}
        role='button'
        tabIndex={0}
      >
        <img
          style={{
            ...imageProps.style,
            width: '225px',
            height: '225px',
            objectFit: 'cover',
          }}
          src={imageProps.src}
          alt={photo.alt}
          draggable={imageProps.draggable}
          onLoad={imageProps.onLoad}
          onError={imageProps.onError}
        />
        {isEdit && isSelected && <SelectedIndicator />}
        {isEdit && isHovered && !isSelected && (
          <div className='card-details-gallery-image'>
            <div style={{ alignItems: 'center' }}>
              <span
                style={{
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: 'bold',
                  paddingRight: '8px',
                }}
              >
                Select
              </span>
              <CircleTwoToneIcon style={{ color: 'white', fontSize: '24px' }} />
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <Modal
      open={galleryOpen}
      onClose={handleGalleryClose}
    >
      <div className='card-details-gallery-modal-container'>
        <div className='card-details-gallery-modal-button-container'>
          <Button
            className='button'
            startIcon={<ArrowBackIcon />}
            onClick={handleGalleryClose}
          >
            {isEdit ? 'Cancel' : 'Back to Details'}
          </Button>
          {isEdit && (
            <Button
              className='button'
              onClick={() => submitCoverImageChange?.(selectedIndex ?? 0)}
              disabled={selectedIndex === originalSelectedImgIndex}
            >
              Save
            </Button>
          )}
        </div>
        {isEdit && <h3>Choose a cover photo:</h3>}
        <div className='card-details-gallery-images-container'>
          {showPlaceholders ? (
            <div className='card-details-gallery-skeleton-grid'>
              {Array.from({ length: placeholderCount }, (_, idx) => (
                <Skeleton
                  // biome-ignore lint/suspicious/noArrayIndexKey: static placeholders
                  key={idx}
                  containerClassName='card-details-gallery-skeleton-tile'
                  className='card-details-gallery-skeleton'
                />
              ))}
            </div>
          ) : isMobile ? (
            // Instagram-style grid for mobile
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(2, 1fr)',
                gap: '2px',
              }}
            >
              {photos.map((photo, idx) => (
                <div
                  key={photo.src}
                  style={{ position: 'relative', aspectRatio: '1/1' }}
                  onClick={() => handlePhotoClick(idx)}
                  onKeyDown={(e) => handleKeyDown(e, idx)}
                  role='button'
                  tabIndex={0}
                >
                  <img
                    src={photo.src}
                    alt=''
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: 'block',
                    }}
                  />
                  {isEdit && selectedIndex === idx && <SelectedIndicator />}
                </div>
              ))}
            </div>
          ) : (
            <PhotoAlbum
              layout='rows'
              photos={photos}
              targetRowHeight={250}
              spacing={4}
              onClick={({ index: current }) => handlePhotoClick(current)}
              renderPhoto={renderPhoto}
            />
          )}
          {!isEdit && (
            <Lightbox
              index={index}
              slides={photos}
              open={index >= 0}
              close={() => setIndex(-1)}
              plugins={[Counter, Zoom, Thumbnails]}
              thumbnails={{
                position: 'bottom',
                width: 150,
                height: 120,
                border: 1,
                borderRadius: 10,
                padding: 4,
                gap: 16,
              }}
              counter={{
                container: { style: { top: isMobile ? 'unset' : 0, bottom: isMobile ? 0 : '' } },
              }}
              zoom={{
                scrollToZoom: true,
                maxZoomPixelRatio: 5,
              }}
              styles={{
                container: { backgroundColor: 'rgba(0, 0, 0, 0.95)' },
                slide: { objectFit: 'cover' },
              }}
              controller={{ closeOnBackdropClick: true }}
              render={{
                buttonPrev: () => null,
                buttonNext: () => null,
              }}
            />
          )}
        </div>
      </div>
    </Modal>
  );
}

CardImageGallery.displayName = 'CardImageGallery';
export default CardImageGallery;
